import classNames from "classnames";
import { useFormik } from "formik";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { ReactComponent as CloseIcon } from "assets/images/common/close.svg";
import { ReactComponent as SuccessIcon } from "assets/images/common/success.svg";

import { useAppDispatch, useAppSelector } from "store/hooks";
import { clearErrors, resetPasswordRequest } from "store/slices/authSlice";

import useValidation from "hooks/useValidation";

import Button from "components/Button";
import InfoCard from "components/InfoCard";
import PasswordInput from "components/PasswordInput";

import LoginTemplate from "modules/login/LoginTemplate";
import * as AuthApi from "services/http/auth";
import { PAGES } from "utils/links";

import { validationSchema } from "./validationSchema";

import styles from "./style.module.scss";

const SetNewPasswordPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isLoading, errors: authErrors } = useAppSelector((state) => state.auth);
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const formRef = useRef<HTMLFormElement>(null);
  const [tokenValid, setTokenValid] = useState<boolean | null>(null);
  const [passwordInfo, setPasswordInfo] = useState({
    isAtLeast8Characters: false,
    isOneUppercaseLetter: false,
    isOneLowercaseLetter: false,
    isOneNumber: false,
    isOneSpecialCharacter: false,
  });

  // Verify token on mount
  useEffect(() => {
    if (!token) {
      navigate(PAGES.LOGIN, { replace: true });
      return;
    }

    AuthApi.verifyResetToken(token)
      .then(() => setTokenValid(true))
      .catch(() => setTokenValid(false));
  }, [token, navigate]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      dispatch(clearErrors());
    };
  }, [dispatch]);

  const form = useFormik({
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    onSubmit: (values) => {
      if (!token) return;

      dispatch(
        resetPasswordRequest({
          token,
          password: values.password,
          confirm_password: values.confirmPassword,
        }),
      );
    },
    validationSchema: validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
  });

  const list = useMemo(() => {
    return [
      {
        isSuccess: passwordInfo.isAtLeast8Characters,
        text: "At least 8 characters",
      },
      {
        isSuccess: passwordInfo.isOneUppercaseLetter,
        text: "One uppercase letter (A-Z)",
      },
      {
        isSuccess: passwordInfo.isOneLowercaseLetter,
        text: "One lowercase letter (a-z)",
      },
      {
        isSuccess: passwordInfo.isOneNumber,
        text: "One number (0-9)",
      },
      {
        isSuccess: passwordInfo.isOneSpecialCharacter,
        text: "One special character (!@#$...)",
      },
    ];
  }, [passwordInfo]);

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const password = e.target.value;
    const isAtLeast8Characters = password.length >= 8;
    const isOneUppercaseLetter = /[A-Z]/.test(password);
    const isOneLowercaseLetter = /[a-z]/.test(password);
    const isOneNumber = /[0-9]/.test(password);
    const isOneSpecialCharacter = /[!@#$%^&*]/.test(password);

    setPasswordInfo({
      isAtLeast8Characters,
      isOneUppercaseLetter,
      isOneLowercaseLetter,
      isOneNumber,
      isOneSpecialCharacter,
    });

    form.setFieldValue("password", password);
  };

  useValidation(formRef, form.errors);

  // Loading while verifying token
  if (tokenValid === null) {
    return (
      <LoginTemplate title="Set your password" description="Verifying your reset link...">
        <div className={styles.loading}>Loading...</div>
      </LoginTemplate>
    );
  }

  // Token is invalid or expired
  if (tokenValid === false) {
    return (
      <LoginTemplate
        title="Link expired"
        description="The reset link is invalid or has expired. Please request a new one."
      >
        <div className={styles.expiredActions}>
          <Link to={PAGES.RESET_PASSWORD}>
            <Button variant="primary">Request</Button>
          </Link>
        </div>
      </LoginTemplate>
    );
  }

  return (
    <LoginTemplate title="Set your password" description={"Create a secure password to access\n your WOLKIS account."}>
      <form ref={formRef} onSubmit={form.handleSubmit} className={styles.form}>
        <PasswordInput
          name="password"
          label="Password"
          value={form.values.password}
          error={form.errors.password}
          onChange={handlePasswordChange}
          placeholder="Enter your password"
        />

        <InfoCard
          size="small"
          variant="secondary"
          className={styles.passwordInfo}
          color={list.every((item) => item.isSuccess) ? "green" : "gray"}
        >
          <p>Password must contain:</p>

          <ul>
            {list.map((item) => (
              <li
                className={classNames(styles.passwordInfoItem, { [styles.passwordInfoSuccess]: item.isSuccess })}
                key={item.text}
              >
                {item.isSuccess ? <SuccessIcon /> : <CloseIcon />}
                <span>{item.text}</span>
              </li>
            ))}
          </ul>
        </InfoCard>

        <PasswordInput
          name="confirmPassword"
          label="Confirm password"
          onChange={form.handleChange}
          className={styles.confirmPassword}
          value={form.values.confirmPassword}
          error={form.errors.confirmPassword}
          placeholder="Re-enter your password"
        />

        {typeof authErrors === "string" && <p className={styles.apiError}>{authErrors}</p>}

        <Button variant="primary" className={styles.button} disabled={isLoading}>
          {isLoading ? "Setting password..." : "Set password"}
        </Button>
      </form>
    </LoginTemplate>
  );
};

export default SetNewPasswordPage;
